import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "hooks/useAxios";
import { USER_API, ENVS } from "config";
import { projectKeys } from "queries";
import { updateSessionInCache } from "hooks/useProfile/useProfileCache";
import { useSessionUser } from "hooks/useSession";
import { useProjectsApi } from "hooks/useProjects";
import { useMemo } from "react";
import type { SessionOneTimePurchases } from "types/session-user";

export interface UseProjectQuotaResult {
  canCreateFromSubscriptionQuota: boolean;
  currentCount: number;
  totalCapacity: number;
  availableProjects: number;
  unlicensedUnderCap: boolean;
  isLoading: boolean;
}

type ProjectQuotaResponse = {
  current_monthly_project_count?: number;
  total_capacity?: number;
  available_projects?: number;
  unlicensed_project_count?: number;
  max_unlicensed_projects?: number;
  one_time_purchases?: Partial<SessionOneTimePurchases> | null;
};

const toSessionOneTimePurchases = (
  otp: Partial<SessionOneTimePurchases>
): SessionOneTimePurchases => ({
  total_licenses: otp.total_licenses ?? 0,
  available_licenses: otp.available_licenses ?? 0,
  used_licenses: otp.used_licenses,
  otp_writer_license_total: otp.otp_writer_license_total,
  otp_studio_license_total: otp.otp_studio_license_total,
  has_available_trial: otp.has_available_trial ?? false,
  has_active_trial: otp.has_active_trial ?? false,
  trial_days_remaining: otp.trial_days_remaining ?? 0,
  trial_expiry_date: otp.trial_expiry_date ?? null,
  latest_purchase_date: otp.latest_purchase_date ?? null,
});

export const useProjectQuota = (): UseProjectQuotaResult => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const { data: session } = useSessionUser();
  const { totalCount: projectsTotalCount } = useProjectsApi({ take: 1, order: "DESC" });

  const { data: quotaQuery, isLoading } = useQuery<ProjectQuotaResponse>({
    queryKey: [...projectKeys.all, "quota"] as const,
    enabled: Boolean(session),
    queryFn: async () => {
      const response = await axios.get(USER_API.getProjectQuota);
      return response.data.data as ProjectQuotaResponse;
    },
    onSuccess: (quota) => {
      if (quota.one_time_purchases) {
        updateSessionInCache(queryClient, (currentSession) => ({
          ...currentSession,
          one_time_purchases: toSessionOneTimePurchases(quota.one_time_purchases ?? {}),
        }));
      }
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const canCreateFromSubscriptionQuota = useMemo(() => {
    if (!session || !quotaQuery) return false;

    if (session.billing_model === "one_time") return false;

    const isFreePlan = session.pricing_plan?.pricing_plan_id === Number(ENVS.freePlanId);
    const isActiveTrial =
      session.subscription?.status === "trialing" &&
      session.subscription?.end_date &&
      new Date(session.subscription.end_date) > new Date();

    const currentCount = Number(quotaQuery?.current_monthly_project_count ?? 0);
    const totalCapacity = Number(quotaQuery?.total_capacity ?? 0);
    const availableProjects = Number(
      quotaQuery?.available_projects ?? Math.max(0, totalCapacity - currentCount)
    );

    const subscriptionAllowsCreation = session.is_active;

    if (!subscriptionAllowsCreation) {
      return false;
    }

    if (isFreePlan) {
      const uc = Number(quotaQuery?.unlicensed_project_count ?? 0);
      const maxU = Number(quotaQuery?.max_unlicensed_projects ?? 10);
      return uc < maxU;
    }

    if (isActiveTrial) {
      const totalProjects = Number(projectsTotalCount || 0);
      return availableProjects > 0 && totalProjects === 0;
    }

    return availableProjects > 0;
  }, [quotaQuery, session, projectsTotalCount]);

  const unlicensedUnderCap = useMemo(() => {
    if (!session) return false;
    const uc = Number(quotaQuery?.unlicensed_project_count ?? 0);
    const maxU = Number(quotaQuery?.max_unlicensed_projects ?? 10);
    return uc < maxU;
  }, [quotaQuery, session]);

  const currentCount = Number(quotaQuery?.current_monthly_project_count ?? 0);
  const totalCapacity = Number(quotaQuery?.total_capacity ?? 0);
  const availableProjects = Number(
    quotaQuery?.available_projects ?? Math.max(0, totalCapacity - currentCount)
  );

  return {
    canCreateFromSubscriptionQuota,
    currentCount,
    totalCapacity,
    availableProjects,
    unlicensedUnderCap,
    isLoading,
  };
};
